import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Clock,
  CheckCircle2,
  PlusCircle,
  ArrowLeft,
  FileText,
  Phone,
  Mail,
} from "lucide-react";
import { fetchMyQuotes } from "../lib/auth";
import { COMPANY } from "../lib/constants";
import { useAuth } from "../lib/AuthContext";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return String(v);
  }
};

const stepCircleClass = (s) => {
  if (s.done) return "bg-emerald-500 border-emerald-500 text-white";
  if (s.active) return "bg-[#FF5A00] border-[#FF5A00] text-white animate-pulse";
  return "bg-[#0E2452] border-[#2F568F] text-white/40";
};
const stepLabelClass = (s) => {
  if (s.done) return "text-emerald-300";
  if (s.active) return "text-[#FF5A00]";
  return "text-white/40";
};
const connectorClass = (s, next) => {
  if (next.done) return "bg-emerald-500/70";
  if (s.done && next.active) return "bg-gradient-to-r from-emerald-500 to-[#FF5A00]";
  return "bg-[#2F568F]/40";
};
const StepIcon = ({ s, index }) => {
  if (s.done) return <CheckCircle2 className="w-4 h-4" />;
  if (s.active) return <Clock className="w-4 h-4" />;
  return <span className="text-[10px] font-display font-bold">{index + 1}</span>;
};
const matchesFilter = (q, filter) => {
  if (filter === "todos") return true;
  const respondido = q.admin_status === "respondido";
  return filter === "aguardar" ? !respondido : respondido;
};

const QuoteTimeline = ({ q }) => {
  const respondido = q.admin_status === "respondido";
  const steps = [
    { key: "sent", label: "Enviado", done: true, at: q.created_at },
    { key: "review", label: "A aguardar resposta", done: respondido, active: !respondido },
    { key: "done", label: "Respondido", done: respondido, at: q.responded_at, isFinal: true },
  ];
  return (
    <div className="mt-4" data-testid={`timeline-${q.id}`}>
      <div className="flex items-center">
        {steps.map((s, i) => {
          const isLast = i === steps.length - 1;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${stepCircleClass(s)}`}>
                  <StepIcon s={s} index={i} />
                </div>
                <div className="mt-1.5 text-center">
                  <div className={`text-[10px] uppercase tracking-widest font-display font-semibold ${stepLabelClass(s)}`}>
                    {s.label}
                  </div>
                  {s.at && (
                    <div className="text-[9px] text-white/40 font-body mt-0.5">{formatDate(s.at)}</div>
                  )}
                </div>
              </div>
              {!isLast && (
                <div className={`flex-1 h-0.5 mx-1 md:mx-2 mb-6 rounded-full ${connectorClass(s, steps[i + 1])}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const OsMeusOrcamentosPage = () => {
  const { user, loading, markNotificationsSeen } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);

  const [filter, setFilter] = useState("todos");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?next=/os-meus-orcamentos", { replace: true });
      return;
    }
    (async () => {
      try {
        const data = await fetchMyQuotes();
        setQuotes(data);
        markNotificationsSeen();
      } catch (err) {
        console.error("[Orçamentos] falha ao carregar pedidos:", err);
        toast.error("Não foi possível carregar os seus orçamentos");
      }
      setLoadingQuotes(false);
    })();
  }, [user, loading, navigate, markNotificationsSeen]);

  if (loading || !user) {
    return (
      <main className="min-h-screen bg-[#0E2452] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#FF5A00] border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  const pendentes = quotes.filter((q) => q.admin_status !== "respondido").length;
  const respondidos = quotes.length - pendentes;
  const visible = quotes.filter((q) => matchesFilter(q, filter));
  const tabs = [
    { key: "todos", label: "Todos", count: quotes.length },
    { key: "aguardar", label: "A aguardar", count: pendentes },
    { key: "respondidos", label: "Respondidos", count: respondidos },
  ];

  return (
    <main className="min-h-screen bg-[#0E2452] pt-32 pb-20 px-6 md:px-12" data-testid="os-meus-orcamentos-page">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/minha-conta"
          data-testid="back-to-account-link"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white font-body text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar à minha conta
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-label mb-1">// Área de cliente</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl uppercase text-white leading-tight">
              Os meus orçamentos
            </h1>
            <p className="text-white/60 text-sm font-body mt-2">
              Acompanhe o estado dos seus pedidos e veja as respostas da oficina.
            </p>
          </div>
          <Link
            to="/orcamento"
            data-testid="quotes-new-quote-btn"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-display font-bold uppercase text-xs tracking-wider px-5 py-3 rounded-full transition-all shrink-0"
          >
            <PlusCircle className="w-4 h-4" />
            Novo pedido
          </Link>
        </div>

        {!loadingQuotes && quotes.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-8" data-testid="quotes-stats">
            <div className="rounded-2xl border border-[#2F568F] bg-[#0B1D44] p-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-display">Total</div>
              <div className="font-display font-bold text-2xl text-white">{quotes.length}</div>
            </div>
            <div className="rounded-2xl border border-[#FF5A00]/40 bg-[#FF5A00]/5 p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A00] font-display">A aguardar</div>
              <div className="font-display font-bold text-2xl text-white">{pendentes}</div>
            </div>
            <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-display">Respondidos</div>
              <div className="font-display font-bold text-2xl text-white">{respondidos}</div>
            </div>
          </div>
        )}

        {!loadingQuotes && quotes.length > 0 && (
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1" data-testid="quotes-filter-tabs" role="tablist">
            {tabs.map((t) => {
              const active = filter === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  data-testid={`quotes-tab-${t.key}`}
                  onClick={() => setFilter(t.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-display font-bold uppercase text-[11px] tracking-wider border transition-all shrink-0 ${
                    active
                      ? "bg-[#FF5A00] border-[#FF5A00] text-white"
                      : "border-[#2F568F] text-white/70 hover:border-[#FF5A00] hover:text-white"
                  }`}
                >
                  {t.label}
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] flex items-center justify-center ${active ? "bg-white/20" : "bg-[#0B1D44]"}`}>
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loadingQuotes ? (
          <div className="text-white/60 text-sm">A carregar...</div>
        ) : quotes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2F568F] p-10 text-center" data-testid="quotes-empty">
            <Package className="w-8 h-8 text-white/40 mx-auto mb-3" />
            <p className="text-white/70 font-body mb-4">Ainda não tem pedidos de orçamento associados a esta conta.</p>
            <Link to="/orcamento" className="btn-primary text-sm py-3 px-6">Pedir Orçamento</Link>
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2F568F] p-10 text-center" data-testid="quotes-filter-empty">
            <Package className="w-8 h-8 text-white/40 mx-auto mb-3" />
            <p className="text-white/70 font-body">
              {filter === "aguardar" ? "Não tem pedidos a aguardar resposta." : "Ainda não tem pedidos respondidos."}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-quotes-list">
            {visible.map((q) => {
              const respondido = q.admin_status === "respondido";
              return (
                <div
                  key={q.id}
                  data-testid={`my-quote-${q.id}`}
                  className="rounded-2xl border border-[#2F568F] bg-[#0B1D44] p-5 md:p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-display font-bold ${
                            respondido
                              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                              : "bg-[#FF5A00]/15 text-[#FF5A00] border border-[#FF5A00]/40"
                          }`}
                        >
                          {respondido ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {respondido ? "Respondido" : "Enviado"}
                        </span>
                        <span className="text-white/40 text-xs font-body">#{q.id.slice(0, 8)}</span>
                      </div>
                      <div className="font-display font-semibold text-white text-lg leading-tight">
                        {q.servico || "Pedido de orçamento"}
                      </div>
                      <div className="text-white/60 text-sm font-body mt-1">
                        {q.veiculo || "—"}{q.matricula ? ` (${q.matricula})` : ""} · {q.medida_pneu || "—"} · Enviado a {formatDate(q.created_at)}
                      </div>

                      {q.admin_status === "respondido" && (
                        <div
                          data-testid={`admin-response-${q.id}`}
                          className="mt-4 rounded-xl bg-emerald-500/5 border border-emerald-500/40 p-4"
                        >
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-display font-bold flex items-center gap-1.5">
                              <CheckCircle2 className="w-3 h-3" /> Resposta da oficina
                            </div>
                            {q.responded_at && (
                              <div className="text-[10px] text-white/40 font-body">{formatDate(q.responded_at)}</div>
                            )}
                          </div>
                          {q.quoted_price != null && (
                            <div className="mb-2 flex items-baseline gap-2">
                              <span className="text-white/50 text-[11px] uppercase tracking-widest font-display">Valor</span>
                              <span className="text-emerald-300 font-display font-bold text-2xl">€ {Number(q.quoted_price).toFixed(2)}</span>
                            </div>
                          )}
                          {q.admin_notes && (
                            <div className="text-sm text-white/85 font-body whitespace-pre-line">{q.admin_notes}</div>
                          )}
                        </div>
                      )}

                      {respondido ? (
                        <div className="mt-4 rounded-xl border border-yellow-400/40 bg-yellow-400/5 p-4" data-testid={`contact-box-${q.id}`}>
                          <p className="text-white/80 text-sm font-body mb-3">
                            Quer falar com a oficina sobre este orçamento?
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <a
                              href={COMPANY.landlineHref}
                              data-testid={`contact-call-${q.id}`}
                              className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-[#0E2452] font-display font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-full transition-all active:scale-95"
                              style={{ boxShadow: "0 4px 14px rgba(250, 204, 21, 0.3)" }}
                            >
                              <Phone className="w-4 h-4" />
                              Ligar · {COMPANY.landline}
                            </a>
                            <a
                              href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(`Orçamento #${q.id.slice(0, 8)} — ${q.servico || "Pedido"}`)}`}
                              data-testid={`contact-email-${q.id}`}
                              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-[#0E2452] font-display font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-full transition-all active:scale-95"
                            >
                              <Mail className="w-4 h-4" />
                              Enviar email
                            </a>
                          </div>
                        </div>
                      ) : (
                        <p className="text-white/40 text-xs font-body mt-3 italic">
                          A oficina irá analisar o pedido e responder por email. Acompanhe aqui o estado.
                        </p>
                      )}
                    </div>
                  </div>

                  <QuoteTimeline q={q} />
                </div>
              );
            })}
          </div>
        )}

        {!loadingQuotes && quotes.length > 0 && (
          <div className="mt-8 flex items-center gap-2 text-white/40 text-xs font-body">
            <FileText className="w-3.5 h-3.5" />
            {quotes.length} {quotes.length === 1 ? "pedido registado" : "pedidos registados"} nesta conta
          </div>
        )}
      </div>
    </main>
  );
};

export default OsMeusOrcamentosPage;
